import type { NotificationTarget } from "@productlane/lib/src/browser"

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null
}

/** Reads the stored target defensively: rows written by older or newer servers may differ. */
export function readNotificationTarget(payload: unknown): NotificationTarget {
	const target = isRecord(payload) ? payload.target : undefined
	if (!isRecord(target) || typeof target.kind !== "string")
		return { kind: "none" }
	switch (target.kind) {
		case "thread":
			return typeof target.threadId === "string"
				? { kind: "thread", threadId: target.threadId }
				: { kind: "none" }
		case "issue":
			return typeof target.issueId === "string"
				? { kind: "issue", issueId: target.issueId }
				: { kind: "none" }
		case "project":
			return typeof target.projectId === "string"
				? { kind: "project", projectId: target.projectId }
				: { kind: "none" }
		case "docsArticle":
			return typeof target.articleId === "string"
				? { kind: "docsArticle", articleId: target.articleId }
				: { kind: "none" }
		case "docsProposals":
			return { kind: "docsProposals" }
		case "settings":
			return typeof target.path === "string"
				? { kind: "settings", path: target.path }
				: { kind: "none" }
		default:
			return { kind: "none" }
	}
}

/** In-app route for a target, or null when the notification has nowhere to go. */
export function notificationTargetPath(
	target: NotificationTarget,
): string | null {
	switch (target.kind) {
		case "thread":
			return `/threads/${target.threadId}`
		case "issue":
			return `/issues/${target.issueId}`
		case "project":
			return `/projects/${target.projectId}`
		case "docsArticle":
			return `/edit-docs/review/${target.articleId}`
		case "docsProposals":
			return "/edit-docs"
		case "settings":
			// Only app-internal paths; react-router 404s on anything with a scheme.
			return target.path.startsWith("/") && !target.path.startsWith("//")
				? target.path
				: null
		case "none":
			return null
	}
}

/**
 * Reads a group key back as its target, for a link to a group the viewer has no rows in (a teammate's).
 * Mirrors notificationGroupKey: a bare key is a thread id, or a workspace row's id, which then reads as not found.
 */
export function notificationKeyTarget(key: string): NotificationTarget {
	const colon = key.indexOf(":")
	if (colon === -1) {
		return key ? { kind: "thread", threadId: key } : { kind: "none" }
	}
	const entityId = key.slice(colon + 1)
	if (!entityId) return { kind: "none" }
	switch (key.slice(0, colon)) {
		case "thread":
			return { kind: "thread", threadId: entityId }
		case "issue":
			return { kind: "issue", issueId: entityId }
		case "project":
			return { kind: "project", projectId: entityId }
		default:
			return { kind: "none" }
	}
}

/** For rows stored before a type carried a target, or types that never had one. */
const FALLBACK_PATHS: Record<string, string> = {
	"billing.payment_failed": "/settings/plans",
	"ai.cap_reached": "/settings/ai/usage",
	"import.zendesk_finished": "/settings/zendesk-import",
	"import.zendesk_failed": "/settings/zendesk-import",
}

export type NotificationDestination =
	| { kind: "pane"; target: NotificationTarget }
	| { kind: "page"; path: string }
	| { kind: "none" }

/** Threads, issues and projects open beside the list; everything else goes to its own page. */
export function notificationDestination(row: {
	type: string
	payload: unknown
}): NotificationDestination {
	const target = readNotificationTarget(row.payload)
	if (
		target.kind === "thread" ||
		target.kind === "issue" ||
		target.kind === "project"
	) {
		return { kind: "pane", target }
	}
	const path = notificationTargetPath(target) ?? FALLBACK_PATHS[row.type]
	return path ? { kind: "page", path } : { kind: "none" }
}

/** Where a desktop popup's click lands: the notification's own page, else the list. */
export function notificationClickPath(row: {
	type: string
	payload: unknown
}): string {
	const destination = notificationDestination(row)
	if (destination.kind === "page") return destination.path
	if (destination.kind === "pane") {
		return notificationTargetPath(destination.target) ?? "/notifications"
	}
	return "/notifications"
}
